import React, { useMemo } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Extrahiert automatisch die ersten 3 aussagekräftigen Kernpunkte aus dem
 * Lektions-HTML — Priorität:
 *   1. Erste 3 <li> aus Listen (meist die Lernziele/Kernaussagen)
 *   2. Erste 3 <h3>/<h4> Überschriften
 *   3. Erste 3 <p>-Sätze (max 140 Zeichen pro Punkt)
 *
 * Rendert eine goldene Info-Box „Das Wichtigste auf einen Blick".
 * Rendert nichts, wenn weniger als 2 Punkte gefunden wurden.
 */
const extractKeyPoints = (html) => {
  if (!html || typeof html !== 'string') return [];
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // 1. <li>-Elemente aus <ul>/<ol>
    const liNodes = Array.from(doc.querySelectorAll('li'))
      .map((n) => (n.textContent || '').trim())
      .filter((t) => t.length >= 15 && t.length <= 220);
    if (liNodes.length >= 3) return liNodes.slice(0, 3);

    // 2. Überschriften
    const headings = Array.from(doc.querySelectorAll('h3, h4'))
      .map((n) => (n.textContent || '').trim())
      .filter((t) => t.length >= 8 && t.length <= 160);
    if (liNodes.length + headings.length >= 3) {
      return [...liNodes, ...headings].slice(0, 3);
    }

    // 3. Fallback: erste Absätze
    const paragraphs = Array.from(doc.querySelectorAll('p'))
      .map((n) => (n.textContent || '').trim())
      .filter((t) => t.length >= 40)
      .map((t) => (t.length > 140 ? t.slice(0, 137).trim() + '…' : t));

    return [...liNodes, ...headings, ...paragraphs].slice(0, 3);
  } catch {
    return [];
  }
};

export const KeyPointsBox = ({ contentHtml, testId = 'key-points-box' }) => {
  const points = useMemo(() => extractKeyPoints(contentHtml), [contentHtml]);
  if (!points || points.length < 2) return null;

  return (
    <div
      data-testid={testId}
      className="mt-6 rounded-xl border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-500/[0.09] via-black/40 to-amber-500/[0.05] p-5 shadow-lg shadow-yellow-500/5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-yellow-400" />
        <h4 className="text-yellow-300 font-black text-sm uppercase tracking-wide">
          Das Wichtigste auf einen Blick
        </h4>
      </div>
      <ul className="space-y-2">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-3 text-gray-200 text-sm leading-relaxed">
            <span className="mt-1 inline-flex w-5 h-5 rounded-full bg-yellow-500 text-black font-black text-[11px] items-center justify-center shrink-0">
              {i + 1}
            </span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default KeyPointsBox;
